/**
 * Watchlist Service - Smart keyword tracking over transaction descriptions
 *
 * Lets analytics "spot" named things automatically: crypto buys (btc, solana,
 * zeus...), AI subscriptions (claude, chatgpt, gemini...), or any user-defined
 * keyword group. Pure local feature: groups persist to localStorage, matching
 * runs over IndexedDB transactions already loaded for the analytics range.
 *
 * Matching rules:
 * - Case-insensitive.
 * - Latin/numeric terms match on word boundaries only ("sol" must NOT match
 *   "gasoline" or "solution"; "eth" must not match "whether").
 * - CJK/Thai terms match as plain substrings (those scripts have no word
 *   boundaries, so boundary checks would break them).
 * - Balance adjustments ([adjustment] descriptions) are excluded, same as the
 *   rest of analytics.
 * - Transfers are tracked separately (a "Transfer to Coinbase" is relevant to
 *   a Crypto group but is not spending).
 *
 * Performance: O(n * terms) over the analytics date range, computed in one
 * pass and memoized by the caller.
 */

import type { Transaction } from '@services/db';

// ============================================================
// Types
// ============================================================

export interface WatchGroup {
  id: string;
  name: string;      // User data (like account names) - not translated
  icon: string;      // Emoji shown on the card
  terms: string[];   // Stored lowercase, trimmed, deduped
  enabled: boolean;
  /**
   * Capital-move mode: matched transactions are asset reallocation (e.g.
   * moving funds to an external Solana/DeFi wallet recorded as "expense"),
   * not real spending. Analytics excludes them from spending totals, the
   * cash flow score, and insights - the Watchlist card still shows them.
   */
  excludeFromSpending?: boolean;
}

export interface WatchMatch {
  id: string;
  description: string;
  amount: number;
  type: 'income' | 'expense' | 'transfer';
  date: Date;
  term: string;      // Which term matched
}

export interface WatchGroupStats {
  group: WatchGroup;
  count: number;             // Matched txns (income+expense+transfer)
  expense: number;
  income: number;
  transfer: number;          // Transfer volume touching this group
  net: number;               // income - expense
  monthlyAvgExpense: number; // Normalized by range length
  prevExpense: number;       // Same-length period immediately before
  changePct: number | null;  // Expense change vs prev period (null = no baseline)
  hasBaseline: boolean;      // False when no prev period exists (e.g. All Time)
  topMatches: WatchMatch[];  // Largest matches first
  termHits: Record<string, number>; // term -> match count (only terms that hit)
}

// ============================================================
// Constants
// ============================================================

const STORAGE_KEY = 'ninjacash-watchlist';
// v2 (2026-07-18): seeded Crypto group defaults to excludeFromSpending=true -
// crypto-term "expenses" are asset moves, not consumption. Migration flips
// the flag on v1-seeded profiles so nobody has to discover a checkbox before
// the labels/stats stop calling capital moves "spent" (owner report).
const STORAGE_VERSION = 2;

export const WATCHLIST_LIMITS = {
  maxGroups: 12,
  maxTermsPerGroup: 30,
  maxTermLength: 40,
  maxNameLength: 30,
  topMatchesShown: 5,
} as const;

/** Seeded once; users can edit or delete them and they stay deleted. */
export const DEFAULT_WATCH_GROUPS: WatchGroup[] = [
  {
    id: 'default-crypto',
    name: 'Crypto',
    icon: '₿', // Bitcoin sign
    terms: [
      'btc', 'bitcoin', 'sol', 'solana', 'zeus', 'eth', 'ethereum',
      'coinbase', 'binance', 'kraken', 'crypto',
    ],
    enabled: true,
    // Capital moves by default: money matched here was moved into crypto,
    // not consumed. Analytics shows it as "Moved out / Still out" and keeps
    // it out of spending totals (summary card notes the excluded amount).
    excludeFromSpending: true,
  },
  {
    id: 'default-ai',
    name: 'AI Tools',
    icon: '\u{1f916}', // Robot face
    // Terms validated against real owner data 2026-07-18 (zai/grok/kimi were
    // real misses; word boundaries keep short ones safe - 'gpt' never matches
    // 'egyptian', 'zai' never matches 'banzai').
    terms: [
      'claude', 'anthropic', 'openai', 'chatgpt', 'gpt', 'codex',
      'gemini', 'copilot', 'midjourney', 'perplexity',
      'grok', 'zai', 'kimi', 'deepseek',
    ],
    enabled: true,
  },
];

interface StoredWatchlist {
  version: number;
  seeded: boolean;   // Defaults were seeded once - never re-seed after delete
  groups: WatchGroup[];
}

// ============================================================
// Persistence
// ============================================================

function cloneDefaults(): WatchGroup[] {
  return DEFAULT_WATCH_GROUPS.map((g) => ({ ...g, terms: [...g.terms] }));
}

function sanitizeGroup(raw: unknown): WatchGroup | null {
  if (!raw || typeof raw !== 'object') return null;
  const g = raw as Partial<WatchGroup>;
  if (typeof g.id !== 'string' || !g.id) return null;
  if (typeof g.name !== 'string' || !g.name.trim()) return null;

  const terms = Array.isArray(g.terms)
    ? Array.from(
        new Set(
          g.terms
            .filter((t): t is string => typeof t === 'string')
            .map((t) => normalizeTerm(t))
            .filter((t) => t.length > 0)
        )
      ).slice(0, WATCHLIST_LIMITS.maxTermsPerGroup)
    : [];

  return {
    id: g.id,
    name: g.name.trim().slice(0, WATCHLIST_LIMITS.maxNameLength),
    icon: typeof g.icon === 'string' && g.icon ? g.icon : '⭐',
    terms,
    enabled: g.enabled !== false,
    excludeFromSpending: g.excludeFromSpending === true,
  };
}

export function normalizeTerm(term: string): string {
  return term.trim().toLowerCase().slice(0, WATCHLIST_LIMITS.maxTermLength);
}

/**
 * v1 -> v2: flip the SEEDED Crypto group to capital-moves mode. Only the
 * default group is touched (matched by its seeded id) - user-created groups
 * and any group the user renamed keep whatever they have. v1 lived for less
 * than a day, so no real user preference is being overridden.
 */
function migrateStoredGroups(groups: WatchGroup[], fromVersion: number): WatchGroup[] {
  if (fromVersion >= STORAGE_VERSION) return groups;

  const migrated = groups.map((g) =>
    g.id === 'default-crypto' && !g.excludeFromSpending
      ? { ...g, excludeFromSpending: true }
      : g
  );
  saveWatchGroups(migrated); // Re-stamps STORAGE_VERSION so this runs once
  return migrated;
}

/** Load groups; seeds defaults on first ever load only. */
export function loadWatchGroups(): WatchGroup[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<StoredWatchlist>;
      const groups = Array.isArray(parsed.groups)
        ? parsed.groups.map(sanitizeGroup).filter((g): g is WatchGroup => g !== null)
        : [];
      if (parsed.seeded) {
        const version = typeof parsed.version === 'number' ? parsed.version : 1;
        return migrateStoredGroups(groups.slice(0, WATCHLIST_LIMITS.maxGroups), version);
      }
    }
  } catch (err) {
    console.error('[Watchlist] Error loading:', err);
  }
  const defaults = cloneDefaults();
  saveWatchGroups(defaults);
  return defaults;
}

export function saveWatchGroups(groups: WatchGroup[]): void {
  try {
    const payload: StoredWatchlist = {
      version: STORAGE_VERSION,
      seeded: true,
      groups: groups.slice(0, WATCHLIST_LIMITS.maxGroups),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    console.error('[Watchlist] Error saving:', err);
  }
}

export function createWatchGroup(name: string): WatchGroup {
  return {
    id: `wg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: name.trim().slice(0, WATCHLIST_LIMITS.maxNameLength),
    icon: '⭐',
    terms: [],
    enabled: true,
  };
}

// ============================================================
// Matching
// ============================================================

// CJK (Hiragana/Katakana/Han) + Thai: no word boundaries in these scripts.
// Ranges: Hiragana/Katakana, CJK Ext A, CJK Unified, CJK Compat, Thai.
const NO_BOUNDARY_SCRIPT = /[぀-ヿ㐀-䶿一-鿿豈-﫿฀-๿]/;

const WORD_CHAR = /[\p{L}\p{N}]/u;

function isWordChar(ch: string | undefined): boolean {
  return !!ch && WORD_CHAR.test(ch);
}

/**
 * True if `text` contains `term` (term must already be normalized lowercase).
 * Latin terms require non-word characters (or edges) on both sides.
 */
export function textMatchesTerm(text: string, term: string): boolean {
  if (!term) return false;
  const lower = text.toLowerCase();

  if (NO_BOUNDARY_SCRIPT.test(term)) {
    return lower.includes(term);
  }

  let idx = lower.indexOf(term);
  while (idx !== -1) {
    const before = idx > 0 ? lower[idx - 1] : undefined;
    const after = lower[idx + term.length];
    if (!isWordChar(before) && !isWordChar(after)) return true;
    idx = lower.indexOf(term, idx + 1);
  }
  return false;
}

/** First matching term of the group for this description, or null. */
export function matchGroup(description: string, group: WatchGroup): string | null {
  if (!description) return null;
  for (const term of group.terms) {
    if (textMatchesTerm(description, term)) return term;
  }
  return null;
}

/**
 * Build a description predicate covering all enabled capital-move groups
 * (excludeFromSpending). Analytics uses it to keep asset reallocation (e.g.
 * "external transfer to solana wallet") out of spending stats while leaving
 * the transactions and account balances untouched.
 * Returns null when no group has the flag - callers skip the check entirely.
 */
export function buildCapitalMoveMatcher(
  groups: WatchGroup[]
): ((description: string) => boolean) | null {
  const flagged = groups.filter(
    (g) => g.enabled && g.excludeFromSpending && g.terms.length > 0
  );
  if (flagged.length === 0) return null;

  return (description: string) => {
    if (!description) return false;
    for (const group of flagged) {
      if (matchGroup(description, group)) return true;
    }
    return false;
  };
}

// ============================================================
// Stats
// ============================================================

function isAdjustment(desc: string | undefined): boolean {
  if (!desc) return false;
  const lower = desc.toLowerCase();
  return lower.includes('[adjustment]') || lower.startsWith('adjustment');
}

/**
 * Compute stats for enabled groups over the current range, with the previous
 * equal-length period used as the trend baseline.
 *
 * @param current  Transactions inside the selected analytics range
 * @param previous Transactions inside the same-length period right before it
 * @param groups   All groups (disabled ones are skipped)
 * @param rangeDays Length of the selected range in days (>=1), for monthly avg
 * @param hasBaseline False when there IS no previous period (All Time) - the
 *                    UI then suppresses trend/new-activity chips entirely
 */
export function computeWatchlistStats(
  current: Transaction[],
  previous: Transaction[],
  groups: WatchGroup[],
  rangeDays: number,
  hasBaseline = true
): WatchGroupStats[] {
  const enabled = groups.filter((g) => g.enabled && g.terms.length > 0);
  if (enabled.length === 0) return [];

  const months = Math.max(1, rangeDays / 30.4375);
  const results: WatchGroupStats[] = [];

  for (const group of enabled) {
    let count = 0;
    let expense = 0;
    let income = 0;
    let transfer = 0;
    const termHits: Record<string, number> = {};
    const matches: WatchMatch[] = [];

    for (const txn of current) {
      if (txn.deletedAt || isAdjustment(txn.description)) continue;
      const term = matchGroup(txn.description, group);
      if (!term) continue;

      count++;
      termHits[term] = (termHits[term] || 0) + 1;
      if (txn.type === 'expense') expense += txn.amount;
      else if (txn.type === 'income') income += txn.amount;
      else if (txn.type === 'transfer') transfer += txn.amount;

      matches.push({
        id: txn.id || '',
        description: txn.description,
        amount: txn.amount,
        type: txn.type as WatchMatch['type'],
        date: txn.date instanceof Date ? txn.date : new Date(txn.date),
        term,
      });
    }

    let prevExpense = 0;
    for (const txn of previous) {
      if (txn.deletedAt || txn.type !== 'expense' || isAdjustment(txn.description)) continue;
      if (matchGroup(txn.description, group)) prevExpense += txn.amount;
    }

    const changePct = hasBaseline && prevExpense > 0
      ? ((expense - prevExpense) / prevExpense) * 100
      : null;

    results.push({
      group,
      count,
      expense,
      income,
      transfer,
      net: income - expense,
      monthlyAvgExpense: expense / months,
      prevExpense,
      changePct,
      hasBaseline,
      topMatches: matches
        .sort((a, b) => b.amount - a.amount)
        .slice(0, WATCHLIST_LIMITS.topMatchesShown),
      termHits,
    });
  }

  // Most active groups first; groups with zero matches sink to the bottom
  // but are still returned so the UI can show "nothing spotted yet".
  return results.sort((a, b) => (b.expense + b.income + b.transfer) - (a.expense + a.income + a.transfer));
}
